import type { SolvariEnv } from "@solvari/utils";

type ProjectLink = {
  href: string;
  icon: string;
  label: string;
};

type ProjectsSection = {
  links: ProjectLink[];
  title: string;
  viewAllLink?: {
    href: string;
    label: string;
  };
};

type ProjectsMenuConfig = {
  sections: ProjectsSection[];
};

type DefaultSupportedLocale = "be-FR" | "be-NL" | "nl-NL";

const configs: Record<DefaultSupportedLocale, ProjectsMenuConfig> = {
  "nl-NL": {
    sections: [
      {
        title: "Projecten voor thuis",
        links: [
          {
            label: "Airconditioning",
            href: "/airconditioning",
            icon: "fa-light fa-air-conditioner",
          },
          {
            label: "Cv-ketel",
            href: "/cv-ketel",
            icon: "fa-light fa-heat",
          },
          {
            label: "Dakisolatie",
            href: "/dakisolatie",
            icon: "fa-light fa-house-circle-check",
          },
          {
            label: "Dakkapel",
            href: "/dakkapel",
            icon: "fa-light fa-house-chimney-window",
          },
          {
            label: "Dakwerken",
            href: "/dakwerk",
            icon: "fa-light fa-house-chimney-blank",
          },
          {
            label: "Kozijnen",
            href: "/kozijnen",
            icon: "fa-light fa-window-frame",
          },
          {
            label: "Laadpaal",
            href: "/laadpaal",
            icon: "fa-light fa-charging-station",
          },
          {
            label: "Muurisolatie",
            href: "/muurisolatie",
            icon: "fa-light fa-block-brick",
          },
          {
            label: "Schuifpui",
            href: "/schuifpui",
            icon: "fa-light fa-window-maximize",
          },
          {
            label: "Stukadoor",
            href: "/stukadoor",
            icon: "fa-light fa-user-helmet-safety",
          },
          {
            label: "Thuisbatterij",
            href: "/thuisbatterij",
            icon: "fa-light fa-battery-bolt",
          },
          {
            label: "Ventilatiesysteem",
            href: "/ventilatiesysteem",
            icon: "fa-light fa-vent-damper",
          },
          {
            label: "Vloerisolatie",
            href: "/vloerisolatie",
            icon: "fa-light fa-house-circle-check",
          },
          {
            label: "Vloerverwarming",
            href: "/vloerverwarming",
            icon: "fa-light fa-temperature-high",
          },
          {
            label: "Vochtbestrijding",
            href: "/vochtbestrijding",
            icon: "fa-light fa-droplet-slash",
          },
          {
            label: "Warmtepomp",
            href: "/warmtepomp",
            icon: "fa-light fa-temperature-high",
          },
          {
            label: "Waterontharder",
            href: "/waterontharder",
            icon: "fa-light fa-tank-water",
          },
          {
            label: "Zonnepanelen",
            href: "/zonnepanelen",
            icon: "fa-light fa-solar-panel",
          },
        ],
        viewAllLink: {
          label: "Bekijk alle projecten",
          href: "/producten",
        },
      },
      {
        title: "Projecten voor op het werk",
        links: [
          {
            label: "Boekhoudservices",
            href: "/boekhouder",
            icon: "fa-light fa-calculator",
          },
          {
            label: "Payroll services",
            href: "/payroll",
            icon: "fa-light fa-money-bills",
          },
          {
            label: "Webdesign",
            href: "/webdesign",
            icon: "fa-light fa-globe-pointer",
          },
          {
            label: "Koffie",
            href: "/koffie",
            icon: "fa-light fa-coffee-beans",
          },
          {
            label: "Lease auto",
            href: "/lease-auto",
            icon: "fa-light fa-car-side",
          },
          {
            label: "Schoonmaakdiensten",
            href: "/schoonmaakservice",
            icon: "fa-light fa-vacuum",
          },
        ],
        viewAllLink: {
          label: "Bekijk alle zakelijke diensten",
          href: "/pro/zakelijke-diensten",
        },
      },
    ],
  },
  "be-NL": {
    sections: [
      {
        title: "Projecten voor thuis",
        links: [
          {
            label: "Airconditioning",
            href: "/nl/airconditioning",
            icon: "fa-light fa-air-conditioner",
          },
          {
            label: "Buitengevelisolatie",
            href: "/nl/buitengevelisolatie",
            icon: "fa-light fa-block-brick",
          },
          {
            label: "CV-ketel",
            href: "/nl/cv-ketel",
            icon: "fa-light fa-heat",
          },
          {
            label: "Dakisolatie",
            href: "/nl/dakisolatie",
            icon: "fa-light fa-house-circle-check",
          },
          {
            label: "Dakwerken",
            href: "/nl/dakwerk",
            icon: "fa-light fa-house-chimney-blank",
          },
          {
            label: "Elektricien",
            href: "/nl/elektricien",
            icon: "fa-light fa-plug-circle-bolt",
          },
          {
            label: "Laadpaal",
            href: "/nl/laadpaal",
            icon: "fa-light fa-charging-station",
          },
          {
            label: "Loodgieter",
            href: "/nl/loodgieter",
            icon: "fa-light fa-user-helmet-safety",
          },
          {
            label: "Ramen en deuren",
            href: "/nl/ramen-en-deuren",
            icon: "fa-light fa-window-frame",
          },
          {
            label: "Schilder",
            href: "/nl/schilder",
            icon: "fa-light fa-brush",
          },
          {
            label: "Spouwmuurisolatie",
            href: "/nl/spouwmuurisolatie",
            icon: "fa-light fa-block-brick",
          },
          {
            label: "Thuisbatterij",
            href: "/nl/thuisbatterij",
            icon: "fa-light fa-battery-bolt",
          },
          {
            label: "Tuinafsluiting",
            href: "/nl/tuinafsluiting",
            icon: "fa-light fa-fence",
          },
          {
            label: "Vloer leggen",
            href: "/nl/nieuwe-vloer-leggen",
            icon: "fa-light fa-house",
          },
          {
            label: "Vloerisolatie",
            href: "/nl/vloerisolatie",
            icon: "fa-light fa-house-circle-check",
          },
          {
            label: "Vochtbestrijding",
            href: "/nl/vochtbestrijding",
            icon: "fa-light fa-droplet-slash",
          },
          {
            label: "Warmtepomp",
            href: "/nl/warmtepomp",
            icon: "fa-light fa-temperature-high",
          },
          {
            label: "Zonnepanelen",
            href: "/nl/zonnepanelen",
            icon: "fa-light fa-solar-panel",
          },
        ],
        viewAllLink: {
          label: "Bekijk alle projecten",
          href: "/nl/producten",
        },
      },
    ],
  },
  "be-FR": {
    sections: [
      {
        title: "Projets pour la maison",
        links: [
          {
            label: "Adoucisseur d'eau",
            href: "/fr/adoucisseur-eau",
            icon: "fa-light fa-tank-water",
          },
          {
            label: "Alarme et sécurité",
            href: "/fr/alarme-securite",
            icon: "fa-light fa-sensor-on",
          },
          {
            label: "Batterie domestique",
            href: "/fr/batterie-domestique",
            icon: "fa-light fa-battery-bolt",
          },
          {
            label: "Borne de recharge",
            href: "/fr/borne-de-recharge",
            icon: "fa-light fa-charging-station",
          },
          {
            label: "Châssis portes et fenêtres",
            href: "/fr/chassis-portes-fenetres",
            icon: "fa-light fa-window-frame",
          },
          {
            label: "Chaudière",
            href: "/fr/chaudiere",
            icon: "fa-light fa-heat",
          },
          {
            label: "Électricité",
            href: "/fr/electricite",
            icon: "fa-light fa-plug-circle-bolt",
          },
          {
            label: "Entrepreneur",
            href: "/fr/entrepreneur",
            icon: "fa-light fa-user-helmet-safety",
          },
          {
            label: "Isolation de la toiture",
            href: "/fr/isolation-toiture",
            icon: "fa-light fa-house-circle-check",
          },
          {
            label: "Isolation des murs creux",
            href: "/fr/isolation-murs-creux",
            icon: "fa-light fa-block-brick",
          },
          {
            label: "Isolation des murs extérieurs",
            href: "/fr/isolation-murs-exterieurs",
            icon: "fa-light fa-block-brick",
          },
          {
            label: "Panneaux solaires",
            href: "/fr/panneaux-solaires",
            icon: "fa-light fa-solar-panel",
          },
          {
            label: "Peinture",
            href: "/fr/peinture",
            icon: "fa-light fa-brush",
          },
          {
            label: "Pompe à chaleur",
            href: "/fr/pompe-a-chaleur",
            icon: "fa-light fa-temperature-high",
          },
          {
            label: "Rénovation de salle de bain",
            href: "/fr/renovation-salle-de-bain",
            icon: "fa-light fa-bath",
          },
          {
            label: "Terrasse et allée",
            href: "/fr/terrasse-et-allee",
            icon: "fa-light fa-trowel-bricks",
          },
          {
            label: "Traitement de l'humidité",
            href: "/fr/traitement-humidite",
            icon: "fa-light fa-droplet-slash",
          },
          {
            label: "Travaux de toiture",
            href: "/fr/toiture",
            icon: "fa-light fa-house-chimney-blank",
          },
        ],
        viewAllLink: {
          label: "Voir tous les projets",
          href: "/fr/produits",
        },
      },
    ],
  },
};

function isSupportedLocale(locale: string): locale is DefaultSupportedLocale {
  return locale in configs;
}

function getProjectsMenuConfig(
  locale: SolvariEnv["config"]["localeCode"],
): ProjectsMenuConfig | null {
  if (!isSupportedLocale(locale)) {
    return null;
  }
  return configs[locale];
}

export { getProjectsMenuConfig };
export type { ProjectLink, ProjectsMenuConfig, ProjectsSection };
